<template>
    <component
        :is="component"
        ref="button"
        :to="to"
        :href="href || resolvedRoute"
        :disabled="disabled"
        :loading="loading"
        class="d-inline-flex align-items-center justify-content-center font--14 font-weight-semi-bold m-0 outline-none rounded-sm transition-button transition-duration-25"
        :class="[
            to | href && 'button',
            loading && 'cursor--wait',
            mode === 'filled' ? 'filled' : 'outline',
            size === 'regular' ? 'btn-regular' : 'btn-mini',
            themeClasses,
            uppercase && 'text-uppercase',
        ]"
        :style="`min-width: ${minWidth}px`"
        @click="clickEvent"
    >
        <template v-if="size === 'mini'">
            <template v-if="!loading">
                <v-icon
                    v-if="icon"
                    :flip="flipIcon"
                    :name="icon"
                    :w="iconSize"
                />

                <slot v-else />
            </template>

            <v-icon
                v-else
                name="loading"
                spin
                :w="iconSize"
            />
        </template>

        <template v-else>
            <div
                v-if="!loading"
                class="d-flex align-items-center text-truncate"
            >
                <v-icon
                    v-if="icon"
                    :class="iconClass"
                    :flip="flipIcon"
                    :name="icon"
                    :w="iconSize"
                />

                <slot />
            </div>

            <template v-else>
                <v-icon
                    name="loading"
                    spin
                    :w="iconSize"
                />

                <span class="ml-4">
                    {{ $t("common.loading") }}
                </span>
            </template>
        </template>
    </component>
</template>

<script setup>
import { computed, toRefs, useTemplateRef } from "vue"
import { useStore } from "vuex"
import { css } from "~/lib/aphrodite"
import { useButton } from "./useButton"

const props = defineProps({
    to: {
        type: [String, Object],
        default: null,
    },
    href: {
        type: String,
        default: "",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    flipIcon: {
        type: String,
        validator: val => {
            const OK = ["horizontal", "vertical"].includes(val)
            if (!OK) console.trace(`Invalid prop: ${val}`)
            return OK
        },
    },
    icon: {
        type: String,
        default: "",
    },
    loading: {
        type: Boolean,
        default: false,
    },
    tag: {
        type: String,
        default: "button",
        validator: val => ["button", "div", "span"].includes(val),
    },
    iconClass: {
        type: String,
        default: "mr-4",
    },
    iconSize: {
        type: Number,
        default: 20,
    },
    minWidth: {
        type: Number,
        default: 0,
    },
    mode: {
        type: String,
        default: "filled",
        validator: val => ["filled", "outline"].includes(val),
    },
    size: {
        type: String,
        default: "regular",
        validator: val => ["regular", "mini"].includes(val),
    },
    theme: {
        type: String,
        default: "primary",
        validator: val => {
            const OK = [
                "primary",
                "success",
                "danger",
                "warning",
                "secondary",
                "dark-veil",
                "light-veil",
                "dark",
                "light",
            ].includes(val)
            if (!OK) console.trace(`Invalid theme ${val}`)
            return OK
        },
    },
    uppercase: {
        type: Boolean,
        default: true,
    },
})

const emits = defineEmits(["click"])
defineExpose({ focus })

const { to, href, tag, loading } = toRefs(props)
const buttonElement = useTemplateRef("button")

const { component, resolvedRoute, clickEvent } = useButton(
    to,
    href,
    tag,
    loading,
    emits
)
const store = useStore()

const themeClasses = computed(() => {
    const { loading, disabled, mode, theme } = props
    const interactive = !disabled && !loading
    const styles = store.getters["theme/styles"]

    switch (theme) {
        case "primary": {
            if (mode === "filled") {
                return [
                    "border-transparent",
                    css(
                        loading ? styles.bgPrimaryLight25 : styles.bgPrimary,
                        styles.textNegative,
                        // active
                        interactive && styles.bgPrimaryDark30Active,
                        // focus
                        interactive && styles.bgPrimaryDark10Focus,
                        interactive && styles.textNegativeDark10Focus,
                        // hover
                        interactive && styles.bgPrimaryDark10Hover,
                        interactive && styles.textNegativeDark10Hover
                    ),
                ]
            } else if (mode === "outline") {
                return [
                    "bg-transparent",
                    css(
                        loading
                            ? styles.borderPrimaryLight25
                            : styles.borderPrimary,
                        loading
                            ? styles.textPrimaryLight25
                            : styles.textPrimary,
                        // active
                        interactive && styles.bgPrimaryDark30Active,
                        interactive && styles.borderPrimaryDark30Active,
                        interactive && styles.textNegativeActive,
                        // focus
                        interactive && styles.bgPrimaryFocus,
                        interactive && styles.textNegativeFocus,
                        // hover
                        interactive && styles.bgPrimaryHover,
                        interactive && styles.textNegativeHover
                    ),
                ]
            } else {
                throw new Error(`Invalid button mode ${mode}`)
            }
        }

        default:
            return !loading
                ? [
                      `${mode}-${theme}`,
                      interactive && `hover:${mode}-${theme}`,
                      interactive && `active:bg-${theme}`,
                  ].filter(Boolean)
                : `${mode}-loading-${theme}`
    }
})

function focus() {
    buttonElement.value.focus()
}
</script>
